import React, { FC, useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { fetchSettingWebBrowser, updateSettingWebBrowser } from '../../../../states/containers/setting/systemSlice'
import { Link, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { RequestPatchSettingWebBrowser, SettingWebBrowserMember } from '../../../../states/types/setting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName } from '@fortawesome/fontawesome-common-types'
import LoadingSquare from '../../../components/common/LoadingSquare'
import MemberListModule from '../../../components/common/MemberListModule'
import SettingModal from '../../../components/common/SettingModal'
import { useAlert } from 'react-alert'
import { RootState } from '../../../../states/containers/store'

const STATUS_LIST = [
  {
    label: "表示しない",
    value: true,
  },
  {
    label: "表示する",
    value: false,
  },
]

const SELECT_LIST = [
  {
    label: "会社全体の設定に準拠する",
    value: "common",
  },
  {
    label: "表示しない",
    value: "on",
  },
  {
    label: "表示する",
    value: "off",
  },
]

const Index: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [hideDetails, setHideDetails] = useState<boolean>(true)

  const [originMemberList, setOriginMemberList] = useState<SettingWebBrowserMember[]>([])
  const [commonValue, setCommonValue] = useState<boolean>(true)

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<SettingWebBrowserMember[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const buildParam = (type: string) => {
    switch (type) {
      case "on":
        return true
      case "off":
        return false
      default:
        return null
    }
  }

  const buildParamToString = (type: boolean | null) => {
    if (type === null) {
      return "common"
    }
    if (type) return "on"
    return "off"
  }

  const changeHideDetails = (data: {commonFlg: boolean, memberIds?: number[], type: string}) => {
    const param: RequestPatchSettingWebBrowser = { change_common_setting: data.commonFlg }
    const hide_details = buildParam(data.type)
    if (data.commonFlg) {
      param.hide_details = hide_details ?? true
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = hide_details === null
      if (data.type !== "common") {
        param.hide_details = hide_details ?? true
      }
    }
    dispatch(updateSettingWebBrowser(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          setCommonValue(hide_details ?? true)
          alert.success("会社全体の設定を変更しました")
        } else {
          setOriginMemberList((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? { ...v, use_common_setting: param.use_common_setting || false, hide_details: param.hide_details ?? null }
              : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchSettingWebBrowser(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common.hide_details)
        setHideDetails(result.common.hide_details)
        setIsLoading(false)
      },
    ))
  }, [])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - Webブラウザの詳細非表示設定`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        Webブラウザの詳細非表示設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          Webブラウザの詳細非表示設定
        </h1>
      </section>
      <section className="page_description">
        <p>
          URLとページタイトルの表示を設定することができます。
        </p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              情報を取得しています…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        会社全体の設定
                      </div>
                    </div>
                    <div className="block-detail">
                      <div
                        className={`graphical_check_box${hideDetails ? " selected" : ""}`}
                        onClick={() => setHideDetails(!hideDetails)}
                        role="none"
                      >
                        <FontAwesomeIcon
                          icon={['far', `${hideDetails ? 'check-square' : 'square'}` as IconName]}
                          className={`icon ${hideDetails ? "checked" : "no_check"}`}
                        />
                        <div>
                          <div className="check_box_title">
                            URLとページタイトルを非表示にする
                          </div>
                          <div className="check_box_description">
                            URLとページタイトルを非表示にすることができます。
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mt-4 button"
                        onClick={() => changeHideDetails({ commonFlg: true, type: hideDetails ? "on" : "off" })}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <MemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, value: v.hide_details }))}
              statusList={STATUS_LIST}
              commonValue={commonValue}
              valueLabel="Webブラウザの詳細非表示設定"
              type="bool"
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingModal
              targetMemberList={targetMembers.map((v) => ({ ...v, value: buildParamToString(v.hide_details) }))}
              selectList={SELECT_LIST}
              label="Webブラウザの詳細非表示設定"
              prefix="URLとページタイトルを"
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { commonFlg: boolean; memberIds: number[]; type: string }) => changeHideDetails(data)}
              closeCallback={closeModal}
            />
          </>
        )
      }
    </section>
  )
}

export default Index
